/**
 * CalendarModal
 * -----------------------------------------------------------------------------
 * Abre o calendário global (mesma tela de /tenant/calendar) em formato de modal,
 * permitindo consultar/criar eventos sem sair da tela atual.
 *
 * Reaproveita 100% o componente `CalendarWorkspace`, logo mantém exatamente as
 * mesmas regras de permissão/visibilidade já aplicadas pelas RPCs da página.
 *
 * @version 1.1.0
 * RELEASE: 2026-10-10 v1.1.0 - BR-OS-031: repassa opções do workspace (sem menu
 *          lateral, data inicial, fontes fixas, filtro/cor por evento, filtros extras)
 *          e título customizável, para reuso em outros módulos.
 * RELEASE: 2026-08-06 v1.0.0 - Criação do modal de calendário (atalho do chat)
 */

import type { ComponentProps, ReactNode } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarWorkspace } from '@/components/calendar/CalendarWorkspace';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

type WorkspaceOptions = Omit<ComponentProps<typeof CalendarWorkspace>, 'embedded'>;

interface CalendarModalProps extends WorkspaceOptions {
  /** Controla a visibilidade do modal */
  open: boolean;
  /** Callback de abertura/fechamento */
  onOpenChange: (open: boolean) => void;
  /** Título do modal (padrão "Calendário") */
  title?: ReactNode;
}

export function CalendarModal({ open, onOpenChange, title = 'Calendário', ...workspace }: CalendarModalProps) {
  const isMobile = useIsMobile();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'p-0 gap-0 overflow-hidden flex flex-col',
          // Mobile: fullscreen | Desktop: quase tela cheia com respiro nas bordas
          isMobile
            ? 'max-w-none w-screen h-[100dvh] rounded-none'
            : 'max-w-[95vw] w-[95vw] h-[90vh]',
        )}
      >
        <DialogHeader className="px-4 py-3 border-b border-border/40 shrink-0">
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>

        {/* Monta o workspace apenas com o modal aberto (evita fetch em background) */}
        <div className="flex-1 min-h-0 p-2">
          {open && <CalendarWorkspace embedded {...workspace} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
